/**
 * Run surface — the customer-facing run page and its parts: RunView,
 * LiveRunView, RunJourney, RunRequest ("Your request"), RunQuestionCard
 * (HITL) and RunResult (the deliverable). The verbose duration phrasings and
 * the SuspensionReplyForm `replyForm` pack are intentionally NOT the compact
 * i18n `formatDuration` — they are product copy, so they live as catalog
 * functions. `replyForm.*` mirrors the package's English defaults verbatim so
 * English renders unchanged; pt-BR translates them.
 */

export const run = {
  // RunView — loading / error / status chrome
  loadingRun: 'Loading run…',
  missingRunId: 'Missing run id.',
  couldNotLoad: 'This run could not be loaded.',
  retrievingDelivery: 'Retrieving your delivery…',
  backToHistory: 'History',
  backToActiveRuns: 'Active runs',
  workingFallback: 'Working…',
  cancelRun: 'Cancel run',
  cancelFailed: 'Cancel failed — the run is still in progress.',
  pausedReadOnly: 'This conversation is paused waiting for a reply. Open it from Active runs to respond.',
  answerAgain: (until: string) => `Your last reply could not be completed. You can answer again until ${until}.`,
  openToAnswer: 'Open to reply',
  awaitingDetailsUnavailable:
    'This run is waiting for your reply, but the question details could not be loaded. Try reloading the page; if it persists, the pause may have expired.',
  runCancelledNotice: 'This run was cancelled.',

  // LiveRunView — progress headline
  headlineReady: 'Ready',
  headlineAwaiting: 'Waiting for your reply',
  headlineResuming: 'Resuming with your reply…',
  headlineCompleted: 'Completed',
  headlineFailed: 'Run failed',
  headlineCancelled: 'Run cancelled',

  // RunJourney — the reduced history timeline
  historyAria: 'Run history',
  progressTitle: 'Progress',
  howItWent: 'How it went',
  waitingFirstUpdate: 'Waiting for the first update…',
  truncatedFoot: (n: number) =>
    `The earliest ${n === 1 ? 'moment was' : `${n} moments were`} trimmed from this history.`,

  // RunRequest — "Your request"
  yourRequest: 'Your request',
  noBriefText: 'No brief text was provided.',
  briefTruncated: 'The stored brief was truncated for archival.',
  attachmentsCount: (n: number) => `${n} attachment${n === 1 ? '' : 's'}`,
  repliesCount: (n: number) => `${n} repl${n === 1 ? 'y' : 'ies'}`,
  questionN: (n: number) => `Question ${n}`,
  agentAskedForInput: 'The agent asked for your input.',
  yourReplyChip: 'Your reply',
  noReplyTimedOut: 'No reply — timed out',
  cancelled: 'Cancelled',

  // RunResult — outcome headlines
  outcomeAria: 'Run outcome',
  errorHeadline: 'This run couldn’t be completed',
  errorFallbackCause: 'Something went wrong along the way.',
  errorNoDeliverable: 'No deliverable was produced — you can start a new run with the same brief.',
  supportMention: 'If you contact support, mention',
  deliveryAria: 'Your delivery',
  deliveredWithNotes: 'Delivered — with notes',
  deliveryReady: 'Your delivery is ready',
  summaryOfWork: 'Summary of the work',
  finishedEmpty: 'The run finished without a message or files.',
  yourFile: 'Your file',
  yourFilesCount: (n: number) => `Your files (${n})`,
  resultBriefingTitle: 'Result briefing',
  showLess: 'Show less',
  expandBriefing: 'Expand briefing',
  downloadFileTitle: (name: string, size: string) => `Download ${name} (${size})`,

  // RunResult — summary strip facts
  finishedAgo: (ago: string) => `Finished ${ago}`,
  took: (duration: string) => `took ${duration}`,
  filesCount: (n: number) => `${n} file${n === 1 ? '' : 's'}`,
  questionsAnswered: (n: number) => `${n} question${n === 1 ? '' : 's'} answered`,
  specialistsInvolved: (n: number) => `${n} specialist${n === 1 ? '' : 's'} involved`,
  referenceSources: (n: number) => `${n} reference source${n === 1 ? '' : 's'}`,

  // RunResult — human file-kind labels (the badge glyph stays untranslated)
  fileKind: {
    pdf: 'PDF document',
    word: 'Word document',
    excel: 'Excel workbook',
    presentation: 'Presentation',
    zip: 'ZIP archive',
    image: 'Image',
    csv: 'CSV data',
    json: 'Data file',
    markdown: 'Document',
    text: 'Text file',
    generic: 'File'
  },

  // RunResult — canonical issue codes → plain language
  issues: {
    payloadTooLarge: 'Some produced files were too large to include, so this delivery contains only part of them.',
    engineTimeout: 'The work ran longer than the allowed time and was stopped.',
    engineCallerHangup: 'The connection to the requester was closed before the work finished.',
    engineProviderStreamDrop: 'The connection to the AI provider dropped mid-run.',
    // fr-docs `engine_tool_loop` (ADR-0024). The canonical message names the
    // unavailable tool, the config knob to fix, and raw Anthropic file ids —
    // all operator detail. Without this mapping that text reaches the
    // customer verbatim, so the wording must stay generic on purpose.
    engineToolLoop:
      'The agent kept trying to use a capability it does not have and could not finish. This is a setup problem on our side, not something you did — please try again or contact support.',
    userCancelled: 'The run was cancelled.'
  },

  // Verbose, customer-facing duration phrasings (composed, not compact)
  durSeconds: (n: number) => `${n} second${n === 1 ? '' : 's'}`,
  durMinutes: (n: number) => `${n} minute${n === 1 ? '' : 's'}`,
  durHours: (n: number) => `${n} hour${n === 1 ? '' : 's'}`,
  durDays: (n: number) => `${n} day${n === 1 ? '' : 's'}`,
  durDaysHours: (days: number, hours: number) =>
    `${days} day${days === 1 ? '' : 's'} ${hours} hour${hours === 1 ? '' : 's'}`,
  durHoursMinutes: (h: number, m: number) => `${h}h ${m}m`,

  // RunQuestionCard — the HITL surface chrome
  questionCardAria: 'The agent has a question for you',
  sendingReply: 'Sending your reply…',
  inputNeeded: 'Your input is needed',
  resumingSub: 'The run picks up again automatically — you can stay on this page.',
  pausedSub: 'The work is paused on a question only you can answer. It continues the moment you reply.',
  questionOfRounds: (round: number, max: number) => `Question ${round} of ${max}`,
  questionRound: (round: number) => `Question ${round}`,
  replyWindowClosed: 'Reply window closed',
  replyWithin: (remaining: string) => `Reply within ${remaining}`,
  resuming: 'Resuming',
  expiredNotice:
    'The time to answer this question has passed. The run will wrap up on its own using documented assumptions — check back here for the result.',
  resumeError: {
    stale: 'This question was already answered (perhaps in another tab or by a colleague).',
    notFound: 'This question is no longer open — the run may have moved on or expired.',
    inFlight: 'Your reply is already being processed — give it a moment.',
    auth: 'Your session expired. Reload the page and sign in again, then resend your reply.',
    network: 'Your reply didn’t reach us — check your connection and try again.',
    other: 'Something went wrong sending your reply — please try again.'
  },

  // SuspensionReplyForm string pack — English == the package's own defaults,
  // so nothing visibly changes in English (`formatBytes` is supplied by the
  // caller from the i18n formatters and is not part of the catalog).
  replyForm: {
    promptLabel: 'Prompt',
    rationaleLabel: 'Rationale',
    suggestionsLabel: 'Suggestions',
    prefillTitle: 'Click to prefill',
    chooseOneLabel: 'Choose one',
    jsonResponseLabel: 'Response (JSON object)',
    yourReplyLabel: 'Your reply',
    replyPlaceholder: 'Type the answer the agent is waiting for…',
    responseMustBeJsonObject: 'Response must be a JSON object.',
    replyMustBeJsonObject: 'Reply must be a JSON object.',
    attachmentsLabel: 'Attachments',
    attachedNone: '0 attached',
    attachedSummary: (count: number, totalBytes: string, capBytes: string) =>
      `${count} attached · ${totalBytes} / ${capBytes}`,
    attachFiles: 'Attach files',
    removeAttachment: (name: string) => `Remove ${name}`,
    perFileCapExceeded: (name: string, cap: string) => `"${name}" exceeds the per-file cap of ${cap}.`,
    totalCapExceeded: (total: string, cap: string) => `Total ${total} would exceed the ${cap} cap.`,
    tooManyFiles: (count: number, max: number) => `${count} files — at most ${max} can be attached.`,
    uploadProgress: (percent: number) => `${percent}%`,
    uploadFailed: 'An upload failed. Retry it, or remove the file.',
    retryUpload: (name: string) => `Retry ${name}`,
    waitingForUploads: 'Waiting for the files to finish uploading…',
    submitReply: 'Submit reply',
    unknownToolNotice:
      "This tool isn't recognised by the playground UI. Reply is forwarded as parsedCommands verbatim.",
    toolInputLabel: 'Tool input',
    replyJsonLabel: 'Reply (JSON object)'
  }
}
